import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';
import { GridCustomFilterCellProps, GridThAttributes } from '@progress/kendo-react-grid';
import cn from 'clsx';
import {
    instanceOfBasicGridSearch,
    instanceOfGridFilter,
    instanceOfGridSearch,
    instanceOfGridSearchExtended,
} from '@components-clean/core/api-grid/api-grid.helper';
import GridColumSearch from '@components-clean/core/grid/grid-colum-search';
import GridCustomFilter from '@components-clean/core/grid/grid-custom-filter';
import {
    ClientGridFilter,
    FilterOperators,
    GridFilter,
    GridSearch,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import GridLovMultiSelectFilter from '@components-clean/core/grid/grid-lov-multi-select-filter';
import { calcOperator, getDataType } from '@components-clean/core/grid/grid.helper';
import styles from '@components-clean/core/grid/style.module.scss';
import { ReadOnlyContextProvider } from '@contexts-clean/read-only-context';
import { useBoolean } from '@hooks/index';

export type FilterCellValueType = string | boolean | null | GridSearch | GridFilter;

export interface ExtendedGridThAttributes extends GridThAttributes {
    className?: string;
}

export interface ExtendedGridCustomFilterCellProps extends GridCustomFilterCellProps {
    clientGrid?: boolean;
    lovData?: string[];
    lovFilterContains?: boolean;
    lovMultiSelect?: boolean;
    filterKey?: string;
    comparisonFilterKey?: string;
    ragColumn?: boolean;
    singleValueRagColumn?: boolean;
    filterDisabled?: boolean;
    multiValueTypeColumn?: boolean;
    thProps?: ExtendedGridThAttributes | null;
    showPlaceholder?: boolean;
    filterClassName?: string;
    evaluationColumn?: boolean;
    hideAdvancedFilter?: boolean;
}

const getSearchValueFromFilterCellValue = (value: FilterCellValueType): string | boolean | null => {
    if (instanceOfGridSearch(value)) return value.search;
    if (instanceOfGridFilter(value)) return value.filters.length > 0 ? value.filters[0].value : null;
    return value;
};

const FilterCustomCell = ({
    field,
    filterType,
    onChange,
    value,
    clientGrid,
    lovData,
    lovFilterContains,
    lovMultiSelect,
    filterKey,
    comparisonFilterKey,
    ragColumn,
    singleValueRagColumn,
    filterDisabled,
    showPlaceholder,
    multiValueTypeColumn,
    thProps,
    filterClassName,
    evaluationColumn,
    hideAdvancedFilter,
}: ExtendedGridCustomFilterCellProps) => {
    const filterAnchor = useRef<HTMLDivElement | null>(null);

    const showFilter = useBoolean(false);
    const advancedFilterApplied = useBoolean(!!(value && typeof value === 'object'));

    const [search, setSearch] = useState<string | boolean | null>(
        getSearchValueFromFilterCellValue(value as string | boolean | null),
    );

    const onClick = () => {
        showFilter.setTrue();
    };

    const onCancel = (e?: MouseEvent<HTMLButtonElement>, clearFilter = true) => {
        if (clearFilter) {
            advancedFilterApplied.setFalse();
            onChange({
                value: '',
                operator: '',
                // @ts-ignore
                syntheticEvent: e,
            });
        }

        showFilter.setFalse();
    };

    const onFilterChange = (data: GridFilter, e: MouseEvent<HTMLButtonElement>) => {
        advancedFilterApplied.setTrue();
        onChange({
            value: data,
            operator: '',
            syntheticEvent: e,
        });
        showFilter.setFalse();
    };

    const onClientFilterChange = (data: ClientGridFilter, e: MouseEvent<HTMLButtonElement>) => {
        advancedFilterApplied.setTrue();
        onChange({
            value: data.filterValues,
            operator: data.operator,
            syntheticEvent: e,
        });
        showFilter.setFalse();
    };

    const onSearchChange = (searchVal: string | boolean | null) => {
        if (clientGrid) {
            onChange({
                value: searchVal !== undefined && searchVal !== '' ? { search: searchVal } : '',
                operator:
                    searchVal !== null && searchVal !== ''
                        ? calcOperator(
                              {
                                  value:
                                      typeof searchVal === 'boolean' ||
                                      filterType === 'numeric' ||
                                      (lovData && lovData.length > 0 && !lovFilterContains)
                                          ? FilterOperators.Equal
                                          : FilterOperators.Contains,
                                  label: '',
                              },
                              filterType,
                              filterKey,
                              comparisonFilterKey,
                          )
                        : '',
                // @ts-ignore
                syntheticEvent: null,
            });
        } else {
            onChange({
                value: searchVal
                    ? { search: searchVal, type: getDataType(filterType), multiValueType: multiValueTypeColumn }
                    : '',
                operator: '',
                // @ts-ignore
                syntheticEvent: null,
            });
        }

        setSearch(searchVal);
    };

    useEffect(() => {
        const filterVal: unknown = value;
        if (
            (filterVal == null ||
                filterVal === '' ||
                (typeof filterVal === 'object' && !instanceOfGridSearch(filterVal))) &&
            search != null
        ) {
            setSearch(null);
        } else if (!(typeof filterVal === 'object')) {
            advancedFilterApplied.setFalse();
        }
    }, [value]);

    return (
        <HeaderTdElement
            columnId={thProps?.columnId || ''}
            {...thProps}
            className={cn(thProps?.className, filterClassName)}
        >
            <ReadOnlyContextProvider>
                <div
                    className={cn(
                        'k-d-flex k-justify-content-end k-align-items-center custom-cell-filter',
                        styles.customCell,
                        {
                            'k-opacity-20': filterDisabled,
                        },
                    )}
                >
                    <div className={styles.searchWrap}>
                        <GridColumSearch
                            value={search}
                            onChange={(e) => onSearchChange(e)}
                            type={filterType}
                            disabled={
                                filterDisabled ||
                                (value
                                    ? clientGrid
                                        ? !instanceOfBasicGridSearch(value)
                                        : !instanceOfGridSearchExtended(value)
                                    : false)
                            }
                            clientGrid={clientGrid}
                            lovData={lovData}
                            showPlaceholder={showPlaceholder}
                        />
                    </div>
                    <div ref={filterAnchor} className={styles.filterWrapper}>
                        {!hideAdvancedFilter && (
                            <button
                                type="button"
                                data-testid={`filter-${field}`}
                                className={cn('k-p-0 custom-cell-filter-btn', styles.filterBtn, {
                                    'k-cursor-pointer': !filterDisabled,
                                    [styles.filterBtnActive]:
                                        ((value != null && value !== '' && !instanceOfGridSearch(value)) ||
                                            showFilter.value) &&
                                        advancedFilterApplied.value,
                                })}
                                onClick={onClick}
                                disabled={filterDisabled}
                            >
                                <span className="k-icon k-font-icon k-i-filter">
                                    <span className="k-hidden">Filter</span>
                                </span>
                            </button>
                        )}
                    </div>
                    {lovMultiSelect === true ? (
                        <GridLovMultiSelectFilter
                            key={field}
                            anchor={filterAnchor.current}
                            popupAlign={{ vertical: 'top', horizontal: 'left' }}
                            show={showFilter.value}
                            filterValue={value}
                            onCancel={onCancel}
                            onSelect={(data, e) => {
                                if (!clientGrid) {
                                    onFilterChange(data as GridFilter, e);
                                }
                            }}
                            lovData={lovData}
                        />
                    ) : (
                        <GridCustomFilter
                            searchText={search}
                            key={field}
                            anchor={filterAnchor.current}
                            dataType={getDataType(filterType)}
                            show={showFilter.value}
                            filterValue={value as (GridFilter | GridSearch) | null}
                            filterKey={filterKey}
                            popupAlign={{ vertical: 'top', horizontal: 'left' }}
                            onCancel={onCancel}
                            onSelect={(data, e) => {
                                clientGrid
                                    ? onClientFilterChange(data as ClientGridFilter, e)
                                    : onFilterChange(data as GridFilter, e);
                            }}
                            clientGrid={clientGrid}
                            lovData={lovData}
                            lovFilterContains={lovFilterContains}
                            ragColumn={ragColumn}
                            singleValueRagColumn={singleValueRagColumn}
                            filterDisabled={filterDisabled}
                            multiValueTypeColumn={multiValueTypeColumn}
                            evaluationColumn={evaluationColumn}
                            hideAdvancedFilter={hideAdvancedFilter}
                        />
                    )}
                </div>
            </ReadOnlyContextProvider>
        </HeaderTdElement>
    );
};

export default FilterCustomCell;
